import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Layout.css";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import ContentArea from "./ContentArea";
import FooterTabs from "./FooterTabs";
import { useWindowContext } from "../Context/WindowContext";
import { useAuth } from "../Components/StoredDetails";
import ClipLoader from "react-spinners/ClipLoader";
import { renderFormComponent } from "./formRegistry";

const placeholders = [
  "Stores Purchase ....",
  "Master Items ....",
  "GRDS/FRMS/Calculation Settings ....",
  "Purchase Details....",
  "PO Details....",
  "AP Invoice....",
  "Bill Of Entry....",
  "Stock Details....",
];


const HomeShell = () => {
  const { windows, activeWindowId, openWindow, closeWindow, setActiveWindow, clearWindows } = useWindowContext();
  const { authData } = useAuth();
  const user = authData?.user;

  const navigate = useNavigate();
  const [loadingPopup, setLoadingPopup] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [masterSearchQuery, setMasterSearchQuery] = useState("");
  const [masterSearchResults, setMasterSearchResults] = useState([]);
  const [menus, setMenus] = useState([]);
  const [index, setIndex] = useState(0);
  const [animateOut, setAnimateOut] = useState(false);
  const initialMobile = typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  const [isMobileView, setIsMobileView] = useState(initialMobile);
  const [sidebarOpen, setSidebarOpen] = useState(!initialMobile);
  const [currentMenuPath, setCurrentMenuPath] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [dashboardResetKey, setDashboardResetKey] = useState(0);

  const [showSessionPopup, setShowSessionPopup] = useState(false);
  const [searchOriginWindowId, setSearchOriginWindowId] = useState(null);
  const [searchOpenedWindowId, setSearchOpenedWindowId] = useState(null);

  const popupRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const INACTIVITY_TIMEOUT = 8 * 60 * 1000;

  const activeWindow = windows.find((w) => w.id === activeWindowId) || windows[0];

  useEffect(() => {
    const loadMenus = async () => {
      if (!user?.UserID) return;

      try {
        const { data: fetchedMenus } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/menus`,
          {
            email: user.UserID,
          }
        );

        if (!Array.isArray(fetchedMenus)) {
          setMenus([]);
          return;
        }

        const menuMap = {};

        fetchedMenus.forEach((menu) => {
          if (Number(menu.ParentCode) === 0) {
            menuMap[menu.MenuCode] = { ...menu, submenus: [] };
          }
        });

        fetchedMenus.forEach((sub) => {
          if (Number(sub.ParentCode) !== 0 && menuMap[sub.ParentCode]) {
            menuMap[sub.ParentCode].submenus.push(sub);
          }
        });

        setMenus(Object.values(menuMap));
      } catch (err) {
        console.error("Error loading menus", err);
      }
    };

    loadMenus();
  }, [user?.UserID]);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimateOut(true);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % placeholders.length);
        setAnimateOut(false);
      }, 250);
    }, 2500);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      if (showSessionPopup) return;

      inactivityTimerRef.current = setTimeout(() => {
        setShowSessionPopup(true);
      }, INACTIVITY_TIMEOUT);
    };

    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer, true);
    });

    resetInactivityTimer();

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer, true);
      });

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [INACTIVITY_TIMEOUT, showSessionPopup]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);

      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSessionExpired = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    sessionStorage.clear();
    clearWindows();
    setShowSessionPopup(false);
    navigate("/");
  };

  const getPageFormName = (item = {}) => {
    return item?.DesktopFrmName || item?.FrmName || item?.MenuName || "";
  };

  const loadComponent = async (formName) => {
    setLoadingPopup(true);

    return new Promise((resolve) => {
      setTimeout(() => {
        setLoadingPopup(false);
        resolve(renderFormComponent(formName, formName || "Unknown"));
      }, 100);
    });
  };

  const handleOpenPage = async (item, parentName = "") => {

    const pageTitle = parentName
      ? `${parentName} - ${item.MenuName}`
      : item.MenuName;

    const newWindowId = `${item.MenuCode}-${Date.now()}`;

    setCurrentMenuPath(pageTitle);

    setSearchOriginWindowId(activeWindowId || searchOriginWindowId);
    setSearchOpenedWindowId(newWindowId);

    const component = await loadComponent(getPageFormName(item));

    openWindow({
      id: newWindowId,
      title: pageTitle,
      content: component,
      pageKey: getPageFormName(item) || item.MenuName,
    });

  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    if (!searchQuery.trim()) {
      setSearchOriginWindowId(activeWindowId || null);
    }

    const results = [];

    menus.forEach((menu) => {
      if (menu.MenuName.toLowerCase().includes(query)) {
        results.push({
          type: "menu",
          name: menu.MenuName,
          data: menu,
        });
      }

      menu.submenus?.forEach((sub) => {
        if (sub.MenuName.toLowerCase().includes(query)) {
          results.push({
            type: "submenu",
            name: `${menu.MenuName} - ${sub.MenuName}`,
            parent: menu,
            data: sub,
          });
        }
      });
    });

    setSearchResults(results);
    setShowDropdown(results.length > 0);
  };

  const handleSearchSelect = (item) => {
    setSearchQuery("");
    setSearchResults([]);
    setMasterSearchQuery("");
    setMasterSearchResults([]);
    setShowDropdown(false);

    if (item.type === "menu") {
      handleOpenPage(item.data);
    } else {
      handleOpenPage(item.data, item.parent?.MenuName);
    }
  };

  const handleMasterSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setMasterSearchQuery(query);

    if (!query.trim()) {
      setMasterSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const results = [];

    menus.forEach((menu) => {
      const menuName = menu.MenuName?.toLowerCase() || "";
      const isMasterMenu = menuName.includes("master");

      if (isMasterMenu && menuName.includes(query)) {
        results.push({
          type: "menu",
          name: menu.MenuName,
          data: menu,
          isMaster: true,
        });
      }

      menu.submenus?.forEach((sub) => {
        const subName = sub.MenuName?.toLowerCase() || "";
        const isMasterSubmenu = subName.includes("master");

        if (isMasterSubmenu && subName.includes(query)) {
          results.push({
            type: "submenu",
            name: `${menu.MenuName} - ${sub.MenuName}`,
            parent: menu,
            data: sub,
            isMaster: true,
          });
        }
      });
    });

    setMasterSearchResults(results);
    setShowDropdown(results.length > 0);
  };

  const handleSearchClear = () => {
    setSearchQuery("");
    setSearchResults([]);
    setMasterSearchQuery("");
    setMasterSearchResults([]);
    setShowDropdown(false);

    if (searchOpenedWindowId) {
      closeWindow(searchOpenedWindowId);
      setSearchOpenedWindowId(null);
    }

    if (searchOriginWindowId) {
      setActiveWindow(searchOriginWindowId);
      setSearchOriginWindowId(null);
    }
  };

  const handleResetToDashboard = () => {
    clearWindows();
    setDashboardResetKey((prev) => prev + 1);
    setCurrentMenuPath("");
    setSearchQuery("");
    setSearchResults([]);
    setMasterSearchQuery("");
    setMasterSearchResults([]);
    setShowDropdown(false);
    setSearchOriginWindowId(null);
    setSearchOpenedWindowId(null);
  };

  return (
    <div>
      {loadingPopup && (
        <div className="loading-overlay">
          <div className="loading-box">
            <ClipLoader size={35} color="#1976d2" />
            <p className="loading-box-text">
              Loading, please wait...
            </p>
          </div>
        </div>
      )}

      <div className="app-shell">
        {isMobileView && sidebarOpen && (
          <div
            className="sidebar-backdrop"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar sidebarOpen={sidebarOpen} onHeaderClick={handleResetToDashboard} />

        <div className="main-panel">
          <Navbar
            title={activeWindow?.title || ""}
            currentMenuPath={currentMenuPath}
            sidebarOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
            searchQuery={searchQuery}
            handleSearch={handleSearch}
            handleSearchSelect={handleSearchSelect}
            masterSearchQuery={masterSearchQuery}
            handleMasterSearch={handleMasterSearch}
            masterSearchResults={masterSearchResults}
            handleMasterSearchSelect={handleSearchSelect}
            placeholders={placeholders}
            index={index}
            animateOut={animateOut}
            searchResults={searchResults}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            setSearchResults={setSearchResults}
            setMasterSearchResults={setMasterSearchResults}
            onSearchClear={handleSearchClear}
            popupRef={popupRef}
            isPopupOpen={isPopupOpen}
            setIsPopupOpen={setIsPopupOpen}
            User={user}
          />

          <ContentArea dashboardResetKey={dashboardResetKey} />
          <FooterTabs />
        </div>
      </div>

      {showSessionPopup && (
        <div className="session-expired-overlay" role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
          <div className="session-expired-modal">
            <div className="session-expired-icon" aria-hidden="true">!</div>
            <p className="session-expired-eyebrow">SECURITY NOTICE</p>
            <h3 id="session-expired-title" className="session-expired-title">
              Session expired
            </h3>
            <p className="session-expired-message">
              Your session expired due to inactivity. Please login again.
            </p>
            <div className="session-expired-actions">
              <button
                className="session-expired-button"
                onClick={handleSessionExpired}
              >
                Return to login
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const Home = () => <HomeShell />;

export default Home;